
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchMyEvents, deleteEvent, EventStatus } from '@/services/eventService';
import { Event } from '@/lib/types';
import { useToast } from "@/hooks/use-toast";
import EventDetailsDrawer from '../EventDetailsDrawer';
import { EventEditorModal, EventCreatorModal } from './event-modals';
import EventsTable from './EventsTable';
import LoadingEventsList from './LoadingEventsList';
import DeleteEventDialog from './DeleteEventDialog';
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { UserRoles } from '@/contexts/auth/enums';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const EventsListPanel = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openEditorModal, setOpenEditorModal] = useState(false);
  const [openCreatorModal, setOpenCreatorModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [activeStatus, setActiveStatus] = useState<string>("all");
  const { toast } = useToast();
  const { user } = useAuth();

  const queryClient = useQueryClient();

  // Use React Query for events data
  const {
    data: events = [],
    isLoading,
    isError,
    error,
    refetch,
    isRefetching
  } = useQuery({
    queryKey: ['events', { userRole: UserRoles.ADMIN }],
    queryFn: () => fetchMyEvents(UserRoles.ADMIN),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Filter events based on status tab
  const getFilteredEvents = () => {
    if (!events.length) return [];

    let filteredEvents = [...events];
    
    if (activeStatus !== "all") {
      filteredEvents = filteredEvents.filter(event => event.status === activeStatus);
    }

    return filteredEvents.sort((a, b) => {
      const dateA = new Date(a.dateStart);
      const dateB = new Date(b.dateStart);
      const now = new Date();

      if ((dateA > now && dateB > now) || (dateA <= now && dateB <= now)) {
        return dateA.getTime() - dateB.getTime();
      }

      return dateA > now ? -1 : 1;
    });
  };

  const filteredEvents = getFilteredEvents();

  // Delete event mutation
  const deleteMutation = useMutation({
    mutationFn: (eventIri: string) => deleteEvent(eventIri),
    onSuccess: () => {
      toast({
        title: "Sikeres törlés!",
        description: "Az esemény sikeresen törölve lett.",
      });
      // Invalidate events query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setIsDeleteDialogOpen(false);
      setSelectedEvent(null);
    },
    onError: (error: Error) => {
      console.error('Error deleting event:', error);
      toast({
        variant: "destructive",
        title: "Hiba történt!",
        description: "Nem sikerült törölni az eseményt.",
      });
      setIsDeleteDialogOpen(false);
    },
  });

  const handleViewEvent = (event: Event) => {
    setSelectedEvent(event);
    setOpenDrawer(true);
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setOpenEditorModal(true);
  };

  const handleCreateEvent = () => {
    setOpenCreatorModal(true);
  };

  const handleDeleteEvent = (event: Event) => {
    setSelectedEvent(event);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteEvent = () => {
    if (selectedEvent?.id) {
      deleteMutation.mutate(selectedEvent.id);
    }
  };

  const handleEventUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  // Get counts for each status
  const getStatusCounts = () => {
    if (!events.length) return { all: 0 };
    
    const counts: Record<string, number> = {
      all: events.length
    };
    
    events.forEach(event => {
      const status = event.status || 'draft';
      counts[status] = (counts[status] || 0) + 1;
    });
    
    return counts;
  };
  
  const statusCounts = getStatusCounts();

  if (isLoading) {
    return <LoadingEventsList />;
  }

  if (isError) {
    return <div className="text-red-500">Error: {error?.message || 'Unknown error occurred'}</div>;
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Események kezelése</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
            Frissítés
          </Button>
          <Button onClick={handleCreateEvent}>
            <Plus className="mr-2 h-4 w-4" />
            Új esemény
          </Button>
        </div>
      </div>

      <Tabs value={activeStatus} onValueChange={setActiveStatus} className="mb-6">
        <TabsList className="mb-4">
          <TabsTrigger value="all">
            Összes ({statusCounts.all || 0})
          </TabsTrigger>
          <TabsTrigger value={EventStatus.DRAFT}>
            Vázlat ({statusCounts[EventStatus.DRAFT] || 0})
          </TabsTrigger>
          <TabsTrigger value={EventStatus.PUBLISHED}>
            Közzétéve ({statusCounts[EventStatus.PUBLISHED] || 0})
          </TabsTrigger>
          <TabsTrigger value={EventStatus.ARCHIVED}>
            Archiválva ({statusCounts[EventStatus.ARCHIVED] || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="m-0">
          {filteredEvents.length > 0 ? (
            <EventsTable
              events={filteredEvents}
              onViewEvent={handleViewEvent}
              onEditEvent={handleEditEvent}
              onDeleteEvent={handleDeleteEvent}
            />
          ) : (
            <div className="text-center py-8 border rounded-md bg-gray-50">
              <p className="text-muted-foreground">Nincs megjeleníthető esemény ebben a kategóriában</p>
            </div>
          )}
        </TabsContent>

        {[EventStatus.DRAFT, EventStatus.PUBLISHED, EventStatus.ARCHIVED].map(status => (
          <TabsContent key={status} value={status} className="m-0">
            {filteredEvents.length > 0 ? (
              <EventsTable
                events={filteredEvents}
                onViewEvent={handleViewEvent}
                onEditEvent={handleEditEvent}
                onDeleteEvent={handleDeleteEvent}
              />
            ) : (
              <div className="text-center py-8 border rounded-md bg-gray-50">
                <p className="text-muted-foreground">Nincs megjeleníthető esemény ebben a kategóriában</p>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <EventDetailsDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        event={selectedEvent}
      />

      <EventEditorModal
        eventId={selectedEvent?.id || null}
        open={openEditorModal}
        onClose={() => setOpenEditorModal(false)}
        onEventUpdated={handleEventUpdated}
      />

      <EventCreatorModal
        open={openCreatorModal}
        onClose={() => setOpenCreatorModal(false)}
        onEventCreated={handleEventUpdated}
      />

      <DeleteEventDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={confirmDeleteEvent}
        isDeleting={deleteMutation.isPending}
      />
    </>
  );
};

export default EventsListPanel;
